import { ReactNode } from "react";
import { DefaultValues, FieldValues, Mode, SubmitHandler, UseFormReset, UseFormReturn, useForm } from "react-hook-form";
import Button from "./Button";
import styles from "./Form.module.css";
import LoadingSpinner from "./LoadingSpinner";
import SubmitBar from "./SubmitBar";

export type FormChildrenProps<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  disabled?: boolean;
};

type Props<TFieldValues extends FieldValues> = {
  disabled?: boolean;
  secondaryButton?: ReactNode;
  onSubmit?: (reset: UseFormReset<TFieldValues>) => SubmitHandler<TFieldValues>;
  submitText?: string;
  defaultValues?: DefaultValues<TFieldValues>;
  children?: ReactNode | ((useFormProps: FormChildrenProps<TFieldValues>) => ReactNode);
  mode?: Mode;
};

const Form = <TFieldValues extends FieldValues>({
  children,
  disabled,
  secondaryButton,
  submitText,
  defaultValues,
  onSubmit,
  mode = "onSubmit",
}: Props<TFieldValues>) => {
  const useFormProps = useForm<TFieldValues>({
    defaultValues,
    mode: mode,
  });

  const isDisabled = disabled || useFormProps.formState.disabled;

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        if (onSubmit) {
          e.stopPropagation();
          useFormProps.handleSubmit(onSubmit(useFormProps.reset))(e);
        }
      }}
    >
      {typeof children === "function" ? children({ ...useFormProps, disabled: isDisabled }) : children}

      {((onSubmit && submitText) || secondaryButton) && (
        <SubmitBar>
          {useFormProps.formState.isSubmitting && <LoadingSpinner />}
          {!!secondaryButton && secondaryButton}
          {onSubmit && submitText && (
            <Button
              buttonProps={{
                type: "submit",
                disabled: isDisabled || useFormProps.formState.isSubmitting,
              }}
            >
              {submitText}
            </Button>
          )}
        </SubmitBar>
      )}
    </form>
  );
};

export default Form;
